// src/components/UserForm.js
import React, { useState } from "react";
import PeoplePicker from "./PeoplePicker";
import { loadConfig } from "../utils/config";

const FALLBACK_ACCESS_LEVELS = ["Admin", "Editor", "Viewer", "Read-Only", "Billing", "Support"];

// Multi-select access level picker
function AccessLevelPicker({ levels, selected, onChange, noun = "role" }) {
  const toggle = (level) => {
    if (selected.includes(level)) {
      onChange(selected.filter(l => l !== level));
    } else {
      onChange([...selected, level]);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {levels.map(level => {
          const active = selected.includes(level);
          return (
            <button
              key={level}
              type="button"
              onClick={() => toggle(level)}
              style={{
                padding: "5px 12px",
                borderRadius: 99,
                background: active ? "var(--purple)" : "var(--gray-100)",
                color: active ? "#fff" : "var(--gray-700)",
                fontSize: 13,
                fontWeight: active ? 600 : 400,
                cursor: "pointer",
                transition: "all 0.1s",
              }}>
              {active && <span style={{ marginRight: 5 }}>✓</span>}
              {level}
            </button>
          );
        })}
      </div>
      {selected.length > 0 && (
        <div style={{ fontSize: 12, color: "var(--gray-500)", marginTop: 6 }}>
          {selected.length} {noun}{selected.length !== 1 ? "s" : ""} selected: <strong>{selected.join(", ")}</strong>
        </div>
      )}
    </div>
  );
}

export default function UserForm({ systemId, user, onClose, onSaved, systemAccessLevels = [], api, levelLabel = "Access Roles", levelNoun = "role", apiSyncWarning = false }) {
  // `api` (e.g. saasApplicationsApi) is required — this form has no
  // built-in default resource type since Systems was removed.
  if (!api) throw new Error("UserForm requires an `api` prop (e.g. saasApplicationsApi) — there is no default resource type.");
  const { get: apiGet, addUser: apiAddUser, updateUser: apiUpdateUser } = api;
  const isEdit = !!user?.id;
  const [configLevels, setConfigLevels] = React.useState(FALLBACK_ACCESS_LEVELS);

  React.useEffect(() => {
    loadConfig().then(cfg => {
      if (cfg.defaultAccessLevels?.length) setConfigLevels(cfg.defaultAccessLevels);
    });
  }, []);

  const accessLevels = systemAccessLevels.length > 0 ? systemAccessLevels : configLevels;

  // Normalise existing accessLevel — could be a string or array
  const normaliseLevel = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    return [val]; // legacy single string
  };

  const [selectedUsers,  setSelectedUsers]  = useState(
    isEdit && user.upn ? [{ displayName: user.displayName, email: user.email, upn: user.upn }] : []
  );
  const [selectedLevels, setSelectedLevels] = useState(normaliseLevel(user?.accessLevel));
  const [notes,          setNotes]          = useState(user?.notes || "");
  const [saving,         setSaving]         = useState(false);
  const [error,          setError]          = useState("");
  const [results,        setResults]        = useState([]);

  const addPerson = (person) => {
    if (!person) return;
    if (selectedUsers.some(u => u.upn === person.upn)) return;
    setSelectedUsers(prev => [...prev, person]);
  };

  const removePerson = (upn) => setSelectedUsers(prev => prev.filter(u => u.upn !== upn));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEdit && selectedUsers.length === 0) { setError("Please select at least one user"); return; }
    if (selectedLevels.length === 0) { setError("Please select at least one access level"); return; }
    setSaving(true);
    setError("");
    setResults([]);

    // Store as array (or single string for backwards compat if only one)
    const accessLevelValue = selectedLevels.length === 1 ? selectedLevels[0] : selectedLevels;

    if (isEdit) {
      try {
        await apiUpdateUser(systemId, user.id, {
          upn: user.upn, displayName: user.displayName, email: user.email,
          accessLevel: accessLevelValue, notes, active: true,
        });
        onSaved();
      } catch (err) { setError(err.message); }
      finally { setSaving(false); }
      return;
    }

    // Multi-add
    const res = [];
    for (const person of selectedUsers) {
      try {
        try {
          const sysData = await apiGet(systemId).catch(() => null);
          if (sysData) {
            const dup = (sysData.users || []).find(
              u => u.active && u.upn?.toLowerCase() === person.upn?.toLowerCase()
            );
            if (dup) {
              const dupLevel = Array.isArray(dup.accessLevel) ? dup.accessLevel.join(", ") : dup.accessLevel;
              res.push({ name: person.displayName, ok: false, error: `Already assigned (${dupLevel})` });
              continue;
            }
          }
        } catch { /* skip duplicate check if it fails — API will reject */ }
        await apiAddUser(systemId, {
          upn: person.upn, displayName: person.displayName,
          email: person.email, accessLevel: accessLevelValue, notes, active: true,
        });
        res.push({ name: person.displayName, ok: true });
      } catch (err) {
        res.push({ name: person.displayName, ok: false, error: err.message });
      }
    }

    setResults(res);
    setSaving(false);

    if (res.some(r => r.ok)) {
      const failedUpns = new Set(
        res.filter((r, i) => !r.ok).map((r, i) => selectedUsers[i]?.upn)
      );
      setSelectedUsers(prev => prev.filter(u => failedUpns.has(u.upn)));
      onSaved();
    }
  };

  const allDone = results.length > 0 && results.every(r => r.ok);

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h2>{isEdit ? "Edit User Access" : "Add Users"}</h2>
          <button className="btn-ghost" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            {apiSyncWarning && (
              <div style={{ background: "var(--orange-light)", color: "#7A3C00", padding: "8px 12px", borderRadius: "var(--radius)", marginBottom: 12, fontSize: 12 }}>
                ⚠ API sync is enabled for this system. If this person is also returned by the connected API, whatever you set here may be overwritten the next time it runs.
              </div>
            )}
            {error && (
              <div style={{ background: "var(--red-light)", color: "var(--red)", padding: "8px 12px", borderRadius: "var(--radius)", marginBottom: 12, fontSize: 13 }}>{error}</div>
            )}

            {results.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                {results.map((r, i) => (
                  <div key={i} style={{
                    display: "flex", alignItems: "center", gap: 8, padding: "7px 10px",
                    borderRadius: "var(--radius)", marginBottom: 4,
                    background: r.ok ? "var(--green-light)" : "var(--red-light)", fontSize: 13,
                  }}>
                    <span>{r.ok ? "✓" : "✗"}</span>
                    <span style={{ fontWeight: 500 }}>{r.name}</span>
                    {!r.ok && <span style={{ color: "var(--red)" }}>— {r.error}</span>}
                  </div>
                ))}
              </div>
            )}

            {/* User selector */}
            <div className="form-group">
              <label>{isEdit ? "User" : "Add People *"}</label>
              {isEdit ? (
                <div style={{ padding: "7px 10px", background: "var(--gray-100)", borderRadius: "var(--radius)", fontSize: 13 }}>
                  <strong>{user.displayName}</strong> <span style={{ color: "var(--gray-500)" }}>({user.upn})</span>
                </div>
              ) : (
                <>
                  <PeoplePicker value={null} onChange={addPerson} placeholder="Search and select a person…" clearOnSelect />
                  {selectedUsers.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                      {selectedUsers.map(u => (
                        <div key={u.upn} style={{
                          display: "inline-flex", alignItems: "center", gap: 6,
                          background: "var(--purple-light)",
                          borderRadius: 99, padding: "4px 10px 4px 12px",
                        }}>
                          <div style={{ width: 20, height: 20, borderRadius: "50%", background: "var(--purple)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700 }}>
                            {u.displayName.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                          </div>
                          <span style={{ fontSize: 13, color: "var(--purple-dark)", fontWeight: 500 }}>{u.displayName}</span>
                          <button type="button" onClick={() => removePerson(u.upn)}
                            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--purple-dark)", fontSize: 14, padding: 0, lineHeight: 1 }}>×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedUsers.length > 0 && (
                    <div style={{ fontSize: 12, color: "var(--gray-500)", marginTop: 6 }}>
                      {selectedUsers.length} person{selectedUsers.length !== 1 ? "s" : ""} selected — all will receive the same roles and notes below.
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Access level multi-picker */}
            <div className="form-group">
              <label>{levelLabel} *</label>
              <AccessLevelPicker
                levels={accessLevels}
                selected={selectedLevels}
                onChange={setSelectedLevels}
                noun={levelNoun}
              />
              {systemAccessLevels.length === 0 && (
                <div style={{ fontSize: 12, color: "var(--gray-500)", marginTop: 6 }}>Using default {levelNoun}s from Configuration.</div>
              )}
            </div>

            {/* Notes */}
            <div className="form-group">
              <label>Notes</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                placeholder="Any notes about this user's access…" />
            </div>

            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={onClose}>
                {allDone ? "Close" : "Cancel"}
              </button>
              <button type="submit" className="btn-primary" disabled={saving || allDone}>
                {saving
                  ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Adding…</>
                  : isEdit ? "Save"
                  : selectedUsers.length > 1 ? `Add ${selectedUsers.length} Users` : "Add User"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
